(() => {
  const positive = value => Number.isFinite(value) && value > 0;
  const solve = (positions, drop) => {
    if (!positions.length || positions.some(p => !positive(p.height) || !positive(p.gap) || p.height <= p.gap)) return null;
    if (drop !== null && (!positive(drop) || positions.some(p => drop >= p.height))) return null;
    const limits = positions.map(p => p.height - p.gap);
    const limit = Math.min(...limits);
    return { limit, limiting: limits.map((v, i) => Math.abs(v - limit) < 0.0001 ? i : -1).filter(i => i >= 0), remaining: positions.map(p => p.height - (drop ?? limit)), drop: drop ?? limit, actual: drop !== null };
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { solve };
  if (typeof customElements === 'undefined' || customElements.get('tls-pdp-space-planner')) return;
  customElements.define('tls-pdp-space-planner', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) { document.addEventListener('change', this.selectionChanged, true); this.bindVariant(); return; }
      this.ready = true;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.read = name => JSON.parse(this.querySelector(`[data-${name}]`)?.textContent || '{}');
      this.copy = this.read('fit-copy');
      this.messages = { ...this.read('messages'), ...this.read('extra-messages') };
      this.actions = this.read('action-messages');
      this.dimensions = this.read('variant-measurements');
      this.reuse = this.read('reuse-source');
      this.reuseDimensions();
      this.dimCopy = this.read('dimension-messages');
      this.examples = this.read('examples');
      this.fixed = this.dataset.installationMode === 'fixed';
      this.variant = this.dataset.selectedVariantId;
      this.unit = this.field('unit').value;
      this.field('scene').value = this.dataset.defaultScene;
      this.active = false;
      this.openingActive = false;
      this.positions = [];
      this.sequence = 0;
      this.classList.add('fit-enhanced');
      this.form.noValidate = true;
      this.querySelector('.tls-pdp__calculator').open = true;
      this.querySelector('[data-scene-controls]').hidden = false;
      this.querySelector('[data-scene-fallback]').hidden = true;
      this.querySelectorAll('button[disabled]').forEach(b => b.disabled = false);
      this.querySelector('[data-result]').hidden = true;
      this.querySelector('[data-comparison-result]').hidden = true;
      this.positionGroup = this.element('div', '', 'fit-position-group');
      this.positionList = this.element('div');
      this.addButton = this.element('button', this.copy.add, 'fit-add');
      this.addButton.type = 'button';
      this.addButton.dataset.addPosition = '';
      this.positionGroup.append(this.positionList, this.addButton, this.element('p', this.copy.position_hint, 'fit-live-note'));
      this.querySelector('[data-unit-hint]').before(this.positionGroup);
      this.querySelector('.tls-pdp__calc-actions').after(this.element('p', this.copy.updated, 'fit-live-note'));
      this.dataNote = this.element('p', '', 'fit-data-note');
      this.querySelector('[data-variant-dimensions]').after(this.dataNote);
      this.card = this.element('section', '', 'fit-card');
      this.card.tabIndex = -1;
      this.card.setAttribute('aria-label', this.copy.title);
      this.badge = this.element('span', '', 'fit-badge');
      this.card.append(this.badge, this.element('h4', this.copy.title));
      this.output = this.element('div');
      this.output.setAttribute('role', 'status');
      this.output.setAttribute('aria-live', 'polite');
      this.output.setAttribute('aria-atomic', 'true');
      this.visual = this.element('div');
      const notes = this.element('details', '', 'tls-pdp__measurement-help');
      notes.append(this.element('summary', this.copy.assumptions), this.element('p', this.copy.formula), this.element('p', this.copy.not_scale));
      this.card.append(this.output, this.visual, notes);
      this.querySelector('.tls-pdp__planner-layout').append(this.card);
      this.form.addEventListener('submit', event => { event.preventDefault(); const first = !this.active; this.active = true; this.calculate(first); });
      this.form.addEventListener('input', event => {
        if (!event.target.matches('input')) return;
        delete event.target.dataset.example;
        this.updateExample();
        if (this.active) this.calculate();
        if (this.openingActive) this.checkOpening();
      });
      this.form.addEventListener('change', event => {
        if (event.target.name === 'unit') this.changeUnit();
        if (event.target.name === 'scene') this.changeScene();
      });
      this.form.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.dataset.sceneChoice) { this.field('scene').value = button.dataset.sceneChoice; this.changeScene(); }
        if (button.hasAttribute('data-load-example')) this.loadExample();
        if (button.hasAttribute('data-clear-basic')) this.clear();
        if (button.hasAttribute('data-add-position')) { this.addPosition(); if (this.active) this.calculate(); }
        if (button.hasAttribute('data-remove-position')) {
          const index = this.positions.findIndex(p => p.id === button.dataset.removePosition);
          if (index >= 0) { this.positions[index].row.remove(); this.positions.splice(index, 1); this.numberPositions(); this.addButton.focus(); if (this.active) this.calculate(); }
        }
        if (button.hasAttribute('data-check-opening')) { this.openingActive = true; this.checkOpening(); }
        if (button.hasAttribute('data-load-opening')) {
          const known = this.measure(this.dimensions[this.variant]?.width);
          this.field('width').value = known ?? this.convert(30);
          this.field('opening').value = (known ?? this.convert(30)) + this.convert(90);
          this.field('opening').dataset.example = 'true';
          if (known === null) this.field('width').dataset.example = 'true';
          this.openingActive = true;
          this.querySelector('[data-opening-notice]').textContent = this.actions.opening_example_loaded;
          this.checkOpening();
        }
        if (button.hasAttribute('data-clear-opening')) {
          for (const name of ['opening', 'width']) { this.field(name).value = ''; delete this.field(name).dataset.example; }
          const known = this.measure(this.dimensions[this.variant]?.width);
          if (known !== null) this.field('width').value = known;
          this.openingActive = false;
          this.querySelector('[data-opening-result]').textContent = '';
          this.querySelector('[data-opening-notice]').textContent = '';
        }
      });
      this.selectionChanged = event => {
        const main = event.target.closest('variant-selects')?.closest('product-info');
        if (!main?.id?.startsWith('MainProduct-') || main.dataset.productId !== this.dataset.productId) return;
        this.variant = '';
        this.variantPending = true;
        this.applyDimensions();
        this.visual.replaceChildren();
        this.output.replaceChildren(this.element('p', this.copy.waiting));
      };
      document.addEventListener('change', this.selectionChanged, true);
      this.bindVariant();
      this.readyVariant = () => this.bindVariant();
      document.addEventListener('DOMContentLoaded', this.readyVariant, { once: true });
      this.refresh();
      this.applyDimensions();
      this.idle();
    }
    element(tag, text = '', className = '') { const e = document.createElement(tag); e.textContent = text; if (className) e.className = className; return e; }
    field(name) { return this.form.elements.namedItem(name); }
    convert(value) { return Number((value * (this.unit === 'cm' ? 2.54 : 1)).toFixed(4)); }
    format(value) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 2 }).format(value); }
    length(value) { return `${this.format(value)} ${this.unit}`; }
    measure(value) {
      const factors = { millimeters: 1, mm: 1, centimeters: 10, cm: 10, meters: 1000, m: 1000, inches: 25.4, in: 25.4, feet: 304.8, ft: 304.8 };
      if (!value || !positive(Number(value.value)) || !factors[String(value.unit).toLowerCase()]) return null;
      return Math.round(Number(value.value) * factors[String(value.unit).toLowerCase()] / (this.unit === 'cm' ? 10 : 25.4) * 10000) / 10000;
    }
    bindVariant() {
      if (this.unsubscribe || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const main = document.getElementById(`MainProduct-${data?.sectionId}`);
        if (!main || main.dataset.productId !== this.dataset.productId || main.dataset.originalSection || !data?.variant) return;
        this.variant = String(data.variant.id);
        this.variantPending = false;
        this.applyDimensions();
        if (this.active) this.calculate(); else this.idle();
        if (this.openingActive) this.checkOpening();
      });
    }
    applyDimensions() {
      const d = this.dimensions[this.variant] || {};
      const facts = [];
      for (const key of ['width', 'height', 'drop']) { const value = this.measure(d[key]); if (value !== null) facts.push(`${key === 'drop' && d.referenceDepth ? this.copy.listed_height + ':' : this.dimCopy[key]} ${this.length(value)}`); }
      const summary = this.querySelector('[data-variant-dimensions]');
      summary.textContent = facts.length ? `${this.copy.known}: ${facts.join(' · ')}` : '';
      summary.hidden = !facts.length;
      for (const name of ['drop', 'width']) { this.field(name).value = this.measure(d[name]) ?? ''; delete this.field(name).dataset.example; }
      this.describeDimensions(d);
      this.updateExample();
      document.dispatchEvent(new CustomEvent('tls:fit-dimensions', { detail: { productId: this.dataset.productId, width: this.measure(d.width), unit: this.unit } }));
    }
    describeDimensions(d) {
      const listed = this.measure(d.listedHeight), suspension = this.measure(d.suspension);
      this.dataNote.textContent = [listed !== null ? `${this.copy.listed_height}: ${this.length(listed)}. ${this.copy.listed_note}` : '', suspension !== null ? `${this.copy.suspension}: ${this.length(suspension)}. ${this.copy.suspension_note}` : '', d.referenceDepth ? this.copy.fixed_reference : '', this.measure(d.drop) === null ? this.copy.manual : ''].filter(Boolean).join(' ');
    }
    reuseDimensions() {
      const source = this.reuse || {}, spec = source.spec || '', body = source.body || '';
      const cm = value => ({ value, unit: 'cm' });
      for (const [id, option] of Object.entries(source.variants || {})) {
        const d = this.dimensions[id] ||= {};
        const variantKey = `V|gid://shopify/ProductVariant/${id}|dimensions|`;
        for (const [label, key] of [['Shade diameter','width'],['Shade body height','height'],['Maximum suspension length shown','suspension']]) {
          const line = spec.split('\n').find(line => line.startsWith(variantKey + label + '|'));
          const match = line?.split('|').at(-1).match(/^(\d+(?:\.\d+)?) cm\b/);
          if (!d[key] && match) d[key] = cm(Number(match[1]));
        }
        if (this.dataset.productId === '10183960723649') {
          const table = new DOMParser().parseFromString(body, 'text/html');
          const row = [...table.querySelectorAll('tr')].find(row => row.cells[0]?.textContent.trim() === `${option.count}-Light`);
          const match = row?.cells[1]?.textContent.match(/(\d+(?:\.\d+)?)cm Diameter x (\d+(?:\.\d+)?)cm Height/);
          if (match) { d.width ||= cm(Number(match[1])); d.listedHeight = cm(Number(match[2])); }
        }
        if (this.dataset.productId === '10529649950913' && body.includes('S918d59d25ea143e99e5f08ef66c61bb0q.jpg') && [7,11,17,23].includes(Number(option.count))) {
          d.width ||= cm(({7:50,11:60,17:70,23:80})[option.count]);
          d.listedHeight = cm(200);
        }
        if (this.dataset.productId === '10184016199873' && spec.includes('P|dimensions|Diameter|78.74 cm / 31 in') && spec.includes('P|dimensions|Listed height|40.64 cm / 16 in')) {
          d.width ||= cm(78.74); d.drop ||= cm(40.64); d.referenceDepth = true;
        }
      }
    }
    refresh() {
      const scene = this.field('scene').value;
      this.querySelectorAll('[data-scene-choice]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.sceneChoice === scene)));
      this.querySelector('[data-height-label]').textContent = this.messages.height[scene];
      this.querySelector('[data-gap-label]').textContent = this.messages.gap[scene];
      this.querySelector('[data-scene-note]').textContent = this.messages.note[scene];
      this.querySelector('[data-scene-example]').textContent = this.examples[scene][this.unit];
      this.querySelector('[data-opening-example]').textContent = this.examples.opening[this.unit];
      this.querySelector('button[type=submit]').textContent = this.fixed ? this.actions.calculate_clearance : scene === 'table' ? this.actions.calculate_drop : this.actions.calculate_maximum;
      this.positionGroup.hidden = scene !== 'stair';
      this.querySelector('[data-width-fields]').hidden = scene !== 'stair';
      for (const name of ['opening', 'width']) this.field(name).disabled = scene !== 'stair';
      this.positions.forEach(p => { p.height.disabled = p.gap.disabled = scene !== 'stair'; });
      const island = this.closest('.tls-pdp__inner')?.querySelector('[data-island-layout]');
      if (island) island.hidden = scene !== 'table';
    }
    changeScene() { this.clear(); this.refresh(); }
    addPosition(height = '', gap = '', example = false) {
      const id = `${this.dataset.productId}-${++this.sequence}`;
      const row = this.element('div', '', 'fit-position');
      const heading = this.element('strong');
      row.append(heading);
      const fields = {};
      for (const [name, label, value] of [['height', this.messages.height.stair, height], ['gap', this.messages.gap.stair, gap]]) {
        const input = this.element('input'); input.type = 'number'; input.min = '0.01'; input.step = 'any'; input.inputMode = 'decimal'; input.id = `TLSFit-${id}-${name}`; input.value = value;
        if (example) input.dataset.example = 'true';
        const caption = this.element('label', `${label} (${this.unit})`); caption.htmlFor = input.id;
        row.append(caption, input); fields[name] = input;
      }
      const remove = this.element('button', this.copy.remove); remove.type = 'button'; remove.dataset.removePosition = id;
      row.append(remove); this.positionList.append(row);
      this.positions.push({ id, row, heading, remove, ...fields });
      this.numberPositions();
    }
    numberPositions() { this.positions.forEach((p, i) => { p.heading.textContent = `${this.copy.position} ${i + 2}`; p.remove.textContent = `${this.copy.remove} ${i + 2}`; }); }
    changeUnit() {
      const next = this.field('unit').value;
      const factor = this.unit === 'cm' ? 1 / 2.54 : 2.54;
      for (const input of this.form.querySelectorAll('input[type=number]')) if (Number.isFinite(input.valueAsNumber)) input.value = Number((input.valueAsNumber * factor).toFixed(4));
      this.unit = next;
      this.positions.forEach(p => p.row.querySelectorAll('label').forEach(label => { label.textContent = label.textContent.replace(/\((in|cm)\)$/, `(${next})`); }));
      this.refresh();
      const summary = this.querySelector('[data-variant-dimensions]');
      const d = this.dimensions[this.variant] || {};
      const facts = ['width', 'height', 'drop'].filter(k => this.measure(d[k]) !== null).map(k => `${k === 'drop' && d.referenceDepth ? this.copy.listed_height + ':' : this.dimCopy[k]} ${this.length(this.measure(d[k]))}`);
      summary.textContent = facts.length ? `${this.copy.known}: ${facts.join(' · ')}` : '';
      this.describeDimensions(d);
      if (this.active) this.calculate();
      if (this.openingActive) this.checkOpening();
    }
    loadExample() {
      const first = !this.active;
      const scene = this.field('scene').value;
      const values = scene === 'table' ? [108, 30] : scene === 'stair' ? [144, 96] : [120, 84];
      ['height', 'gap'].forEach((name, i) => { this.field(name).value = this.convert(values[i]); this.field(name).dataset.example = 'true'; });
      this.positions.forEach(p => p.row.remove()); this.positions = [];
      if (scene === 'stair') this.addPosition(this.convert(132), this.convert(96), true);
      this.applyDimensions();
      this.active = true;
      this.calculate(first);
    }
    clear() {
      this.active = false;
      for (const name of ['height', 'gap', 'drop']) { this.field(name).value = ''; delete this.field(name).dataset.example; }
      this.positions.forEach(p => p.row.remove()); this.positions = [];
      this.applyDimensions();
      this.idle();
    }
    updateExample() {
      const hasExample = [...this.form.querySelectorAll('input[data-example]')].some(i => !i.disabled && !['opening', 'width'].includes(i.name));
      this.badge.textContent = hasExample ? this.copy.example : this.copy.yours;
      this.querySelector('[data-example-notice]').textContent = hasExample ? this.copy.mixed : '';
    }
    idle() { this.card.dataset.state = 'ready'; this.output.replaceChildren(this.element('p', this.copy.ready)); this.visual.replaceChildren(); this.updateExample(); }
    calculate(move = false) {
      if (this.variantPending) return;
      this.updateExample();
      const scene = this.field('scene').value;
      const positions = [{ height: this.field('height').valueAsNumber, gap: this.field('gap').valueAsNumber }];
      if (scene === 'stair') this.positions.forEach(p => positions.push({ height: p.height.valueAsNumber, gap: p.gap.valueAsNumber }));
      const dropField = this.field('drop');
      const drop = dropField.value === '' && !dropField.validity.badInput ? null : dropField.valueAsNumber;
      const result = solve(positions, drop);
      this.output.replaceChildren(); this.visual.replaceChildren();
      if (!result) { this.card.dataset.state = 'error'; this.output.append(this.element('p', this.copy.invalid)); return; }
      const below = result.actual && positions.some((p, i) => result.remaining[i] + 0.0001 < p.gap);
      this.card.dataset.state = below ? 'below' : 'calculated';
      const fixedActual = this.fixed && result.actual;
      const value = fixedActual ? Math.min(...result.remaining) : result.limit;
      const label = fixedActual ? this.copy.remaining : scene === 'table' ? this.copy.target : this.copy.maximum;
      this.output.append(this.element('div', label), this.element('strong', this.length(value), 'fit-value'));
      if (scene === 'stair') this.output.append(this.element('p', `${this.copy.position} ${result.limiting.map(i => i + 1).join(', ')} ${this.copy.limits}.`));
      if (result.actual) {
        const reference = this.dimensions[this.variant]?.referenceDepth && Math.abs(result.drop - this.measure(this.dimensions[this.variant].drop)) < 0.0001;
        this.output.append(this.element('p', `${reference ? this.copy.listed_height : this.copy.installed}: ${this.length(result.drop)}. ${this.copy.remaining}: ${this.length(Math.min(...result.remaining))}.`));
        this.output.append(this.element('p', scene === 'table' ? this.copy.table_note : below ? this.copy.below : this.copy.within));
      } else this.output.append(this.element('p', this.copy.plan));
      this.draw(positions, result);
      if (scene === 'stair') {
        const list = this.element('ol', '', 'fit-positions');
        positions.forEach((p, i) => list.append(this.element('li', `${this.copy.position} ${i + 1}: ${this.copy.clearance} ${this.length(result.remaining[i])} · ${this.copy.chosen} ${this.length(p.gap)}`)));
        this.visual.append(list);
      }
      if (move && window.matchMedia('(max-width: 749px)').matches) { this.card.focus({ preventScroll: true }); this.card.scrollIntoView({ behavior: 'auto', block: 'start' }); }
    }
    draw(positions, result) {
      const ns = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(ns, 'svg'); svg.setAttribute('viewBox', '0 0 420 340'); svg.setAttribute('role', 'img'); svg.setAttribute('aria-label', `${this.copy.diagram}. ${this.copy.installed}: ${this.length(result.drop)}`); svg.classList.add('fit-drawing');
      const add = (tag, attrs, text) => { const el = document.createElementNS(ns, tag); Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v)); if (text) el.textContent = text; svg.append(el); return el; };
      const maxHeight = Math.max(...positions.map(p => p.height));
      const y = value => 42 + value / maxHeight * 210;
      add('text', { x: 210, y: 23, 'text-anchor': 'middle', 'font-size': 15, fill: '#25343d' }, this.copy.ceiling);
      add('path', { d: 'M28 42H392', stroke: '#63786c', 'stroke-width': 4, fill: 'none' });
      const bottom = y(result.drop);
      const scene = this.field('scene').value;
      const person = (x, floor) => {
        add('circle', { cx: x, cy: floor - 75, r: 9, fill: '#6b8796' });
        add('path', { d: `M${x} ${floor-64}v36m0-27l-17 23m17-23l17 23m-17 4l-13 28m13-28l13 28`, stroke: '#6b8796', 'stroke-width': 7, 'stroke-linecap': 'round', fill: 'none' });
      };
      if (scene === 'table') {
        add('rect', { x: 65, y: 252, width: 290, height: 10, rx: 3, fill: '#b89a76' });
        add('path', { d: 'M82 262v44m256-44v44M44 308H376', stroke: '#8f795e', 'stroke-width': 6, fill: 'none' });
        add('ellipse', { cx: 160, cy: 249, rx: 20, ry: 3, fill: '#d9e2df' });
      } else if (scene === 'room') {
        add('path', { d: 'M28 42V252H392V42', stroke: '#ccd6d0', 'stroke-width': 2, fill: 'none' });
        person(92, 249);
      } else {
        const points = positions.map((p, i) => ({ x: 36 + i * 348 / positions.length, y: y(p.height), width: 348 / positions.length - 6 }));
        points.forEach((p, i) => {
          const next = points[i + 1];
          if (next) add('path', { d: `M${p.x+p.width} ${p.y}h6V${next.y}`, stroke: '#63786c', 'stroke-width': 3, fill: 'none' });
          add('path', { d: `M${p.x} ${p.y}v${310-p.y}h${p.width+6}V${p.y}`, stroke: '#d6ded8', fill: '#f1f4f1' });
        });
        person(70, points[0].y - 3);
      }
      if (!this.fixed) add('path', { d: `M210 42V${Math.max(42, bottom - 14)}`, stroke: '#25343d', 'stroke-width': 3, fill: 'none' });
      if (this.fixed) {
        add('path', { d: `M210 42V${bottom-5}M180 ${bottom-5}H240`, stroke: '#8c7348', 'stroke-width': 4, fill: 'none' });
        [180,210,240].forEach(x => add('circle',{cx:x,cy:bottom-5,r:5,fill:'#e4cfaa',stroke:'#5a4c36'}));
      } else if (Number(this.reuse.variants?.[this.variant]?.count) > 1) {
        add('path', { d: `M182 48H238M182 48V${bottom-16}M210 48V${bottom-6}M238 48V${bottom-25}`, stroke:'#8c7348','stroke-width':2,fill:'none' });
        [[182,bottom-16],[210,bottom-6],[238,bottom-25]].forEach(([x,level])=>add('circle',{cx:x,cy:level,r:6,fill:'#e4cfaa',stroke:'#5a4c36'}));
      } else add('path', { d: `M184 ${bottom}Q184 ${bottom-24} 210 ${bottom-24}Q236 ${bottom-24} 236 ${bottom}Z`, fill: '#c9a66b', stroke: '#5a4c36' });
      add('path', { d: `M244 45V${bottom}m-4-4l4 4 4-4M240 49l4-4 4 4`, stroke: '#506c7a', 'stroke-width': 1.5, fill: 'none' });
      add('text', { x: scene === 'stair' ? 210 : 250, y: scene === 'stair' ? bottom + 24 : Math.max(66, (42 + bottom) / 2), 'text-anchor': scene === 'stair' ? 'middle' : 'start', 'font-size': 17, fill: '#506c7a' }, this.length(result.drop));
      const n = positions.length;
      positions.forEach((p, i) => {
        const x = 36 + i * (348 / n); const width = 348 / n - 6; const level = y(p.height);
        add('path', { d: `M${x} ${level}h${width}`, stroke: '#63786c', 'stroke-width': 3, fill: 'none' });
        if (scene === 'stair') {
          const arrowX = x + width / 2, limiting = result.limiting.includes(i), color = limiting ? '#285d46' : '#76838a';
          add('path', { d: `M${arrowX} 44V${level-2}M${arrowX-5} 50l5-6 5 6M${arrowX-5} ${level-8}l5 6 5-6`, stroke: color, 'stroke-width': limiting ? 3 : 1.8, fill: 'none', 'data-position-height': i + 1 });
          add('text', { x: arrowX, y: level + 20, 'text-anchor': 'middle', 'font-size': 16, 'font-weight': limiting ? 700 : 400, fill: color }, String(i + 1));
          const labelX = n <= 3 ? arrowX : 112 + (i % 2) * 196;
          const labelY = n <= 3 ? Math.min(level - 52, 102 + (i % 2) * 46) : 334 + Math.floor(i / 2) * 54;
          add('rect', { x: labelX - 51, y: labelY - 17, width: 102, height: 43, rx: 4, fill: '#fff', stroke: limiting ? color : '#e1e5e2' });
          add('text', { x: labelX, y: labelY, 'text-anchor': 'middle', 'font-size': 15, 'font-weight': limiting ? 700 : 400, fill: color }, `${this.copy.position} ${i + 1}${limiting ? ' *' : ''}`);
          add('text', { x: labelX, y: labelY + 19, 'text-anchor': 'middle', 'font-size': 17, 'font-weight': limiting ? 700 : 400, fill: color }, this.length(p.height));
        } else add('path', { d: `M${x + width / 2} ${bottom}V${level}`, stroke: '#9aa69f', 'stroke-dasharray': '4 4', fill: 'none' });
        if (result.limiting.includes(i)) add('circle', { cx: x + width / 2, cy: level, r: 5, fill: '#285d46' });
      });
      if (scene !== 'stair') {
        add('path',{d:`M300 ${bottom}V252m-4-4l4 4 4-4M296 ${bottom+4}l4-4 4 4`,stroke:'#285d46','stroke-width':1.5,fill:'none'});
        add('text',{x:310,y:(bottom+252)/2,'font-size':15,fill:'#285d46'},this.length(result.remaining[0]));
      }
      const footerY = scene === 'stair' && n > 3 ? 334 + Math.ceil(n / 2) * 54 : 330;
      svg.setAttribute('viewBox', `0 0 420 ${footerY + 10}`);
      if (scene === 'stair') svg.setAttribute('aria-label', `${this.copy.diagram}. ${positions.map((p,i) => `${this.copy.position} ${i+1}: ${this.length(p.height)}${result.limiting.includes(i) ? ', '+this.copy.limits : ''}`).join('. ')}`);
      add('text', { x: 210, y: footerY, 'text-anchor': 'middle', 'font-size': 15, fill: '#25343d' }, scene === 'table' ? this.copy.table : scene === 'stair' ? this.copy.stairs : this.copy.floor);
      this.visual.append(svg, this.element('p', this.copy.schematic, 'fit-live-note'));
    }
    checkOpening() {
      const opening = this.field('opening').valueAsNumber, width = this.field('width').valueAsNumber;
      const output = this.querySelector('[data-opening-result]');
      output.textContent = !positive(opening) || !positive(width) ? this.messages.pair : opening <= width ? this.messages.too_wide : `${this.messages.side} ${this.length((opening - width) / 2)}`;
    }
    disconnectedCallback() { if (this.selectionChanged) document.removeEventListener('change', this.selectionChanged, true); if (this.readyVariant) document.removeEventListener('DOMContentLoaded', this.readyVariant); if (this.unsubscribe) { this.unsubscribe(); this.unsubscribe = null; } }
  });
})();
